import React, { useState, useEffect } from "react";
import Loader from "../../Shared/Loader";
import "../../../css/EditLogDetailsModal.css";
import { LogData } from "./EditLogTable";
import api from "../../../api/axiosConfig";

export interface BookingUser {
  user_id?: string;
  child_id?: string;
  child_name?: string;
  emrId?: string;
  status?: string;
  _id?: string;
}

export interface CancelledSlotBooking {
  slot_id: string;
  date: string;
  slot_time: string;
  bookings: BookingUser[];
}

export interface EditDetailResponse {
  edit_activity_id?: string;
  appointment_cluster_id?: string;
  center_id?: string;
  edited_on?: string;
  dates?: {
    old?: { start_date?: string; end_date?: string };
    new?: { start_date?: string; end_date?: string };
  };
  changed_weekday_templates?: {
    old?: Record<string, string>;
    new?: Record<string, string>;
  };
  weekday_template_map?: {
    old?: Record<string, string>;
    new?: Record<string, string>;
  };
  impacted_slots?: number;
  impacted_bookings?: number;
  cancelled_bookings?: CancelledSlotBooking[];
  created_appointments?: any[];
  [key: string]: any;
}

interface ModalProps {
  logData: LogData;
  onClose: () => void;
}

interface TemplateApiResponse {
  template?: {
    _id: string;
    name: string;
  };
  name?: string;
  data?: { name: string };
}

const formatDateDisplay = (dateStr?: string): string => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const EditLogDetailsModal: React.FC<ModalProps> = ({ logData, onClose }) => {
  const [details, setDetails] = useState<EditDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [templateNames, setTemplateNames] = useState<Map<string, string>>(new Map());
  const [isLoadingNames, setIsLoadingNames] = useState<boolean>(false);

  useEffect(() => {
    const fetchEditDetails = async () => {
      try {
        setIsLoading(true);
        const token = sessionStorage.getItem("accessToken");
        const clusterId = logData.appointmentClusterId || logData.id;
        const versionNumber = logData.editActivityId
          ? logData.editActivityId.replace(/^[^\d]*/, "") || "1"
          : "1";

        let responseData: EditDetailResponse | null = null;

        try {
          // Primary API endpoint: /api/cluster/clusters/{appointment_cluster_id}/edits/{versionNumber}
          const res = await api.appointment_api.get<EditDetailResponse>(
            `/api/cluster/clusters/${clusterId}/edits/${versionNumber}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          responseData = res.data;
        } catch (primaryErr) {
          console.warn("Primary edit details endpoint failed, attempting fallback endpoint...", primaryErr);
          // Fallback endpoint if versioned endpoint is unavailable
          const fallbackRes = await api.appointment_api.get<any>(
            `/api/cluster/clusters/${clusterId}/logs?page=1&limit=50`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const items = fallbackRes.data?.items || fallbackRes.data?.data || [];
          if (items.length > 0) {
            const firstLog = items[0];
            responseData = {
              edit_activity_id: logData.editActivityId,
              appointment_cluster_id: clusterId,
              center_id: logData.centerId,
              edited_on: firstLog.edit_time || firstLog.updated_at,
              dates: firstLog.changes?.screening_start_date
                ? {
                    old: { start_date: firstLog.changes.screening_start_date.old, end_date: firstLog.changes.screening_end_date?.old },
                    new: { start_date: firstLog.changes.screening_start_date.new, end_date: firstLog.changes.screening_end_date?.new },
                  }
                : undefined,
              changed_weekday_templates: firstLog.changes?.weekday_template_map,
              impacted_slots: firstLog.changes?.slots_deleted?.new || 0,
              impacted_bookings: firstLog.impacted_bookings?.length || 0,
              cancelled_bookings: firstLog.impacted_bookings,
            };
          }
        }

        setDetails(responseData);
      } catch (err: any) {
        console.error("Error fetching log details:", err);
        setError(err.message || "Failed to load log details.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchEditDetails();
  }, [logData]);

  // Fetch template names for changed_weekday_templates
  useEffect(() => {
    const fetchTemplateNames = async () => {
      const templatesObj = details?.changed_weekday_templates || details?.weekday_template_map;
      if (!templatesObj) return;

      setIsLoadingNames(true);
      const allIdsSet = new Set<string>();

      if (templatesObj.old) {
        Object.values(templatesObj.old).forEach((id) => id && id !== "none" && allIdsSet.add(id));
      }
      if (templatesObj.new) {
        Object.values(templatesObj.new).forEach((id) => id && id !== "none" && allIdsSet.add(id));
      }

      const uniqueIds = Array.from(allIdsSet);
      if (uniqueIds.length === 0) {
        setIsLoadingNames(false);
        return;
      }

      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        setIsLoadingNames(false);
        return;
      }

      const fetchName = async (id: string) => {
        try {
          const res = await api.appointment_api.get<TemplateApiResponse>(
            `/api/slot-templates/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const name = res.data?.template?.name || res.data?.name || res.data?.data?.name || id;
          return { id, name };
        } catch {
          return { id, name: id };
        }
      };

      try {
        const results = await Promise.all(uniqueIds.map(fetchName));
        const nameMap = new Map<string, string>();
        results.forEach((r) => nameMap.set(r.id, r.name));
        setTemplateNames(nameMap);
      } catch (err) {
        console.error("Error fetching slot template names:", err);
      } finally {
        setIsLoadingNames(false);
      }
    };

    fetchTemplateNames();
  }, [details]);

  const getTemplateDisplayValue = (id?: string | null): React.ReactNode => {
    if (!id || id === "none") return "none";
    if (isLoadingNames) return <Loader size="small" text="" />;
    return templateNames.get(id) || id;
  };

  const templatesObj = details?.changed_weekday_templates || details?.weekday_template_map;
  const oldTemplateMap = templatesObj?.old || {};
  const newTemplateMap = templatesObj?.new || {};

  const daysOfWeekOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const getSortedDays = (map: Record<string, string>) => {
    const keys = Object.keys(map);
    if (keys.length === 0) return [];
    const hasStandardDays = keys.some((k) => daysOfWeekOrder.includes(k));
    if (hasStandardDays) {
      return daysOfWeekOrder.filter((d) => d in map);
    }
    return keys;
  };

  const oldDayKeys = getSortedDays(oldTemplateMap);
  const newDayKeys = getSortedDays(newTemplateMap);

  const hasDatesSection = !!details?.dates && (!!details.dates.old || !!details.dates.new);
  const hasTemplateSection = Object.keys(oldTemplateMap).length > 0 || Object.keys(newTemplateMap).length > 0;

  return (
    <div className="modal-backdrop-log" onClick={onClose}>
      <div className="log-details-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="log-modal-header-container">
          <h2>Appointment Edit Details</h2>
          <button className="global-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {isLoading ? (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <Loader text="Fetching appointment edit details..." />
          </div>
        ) : error ? (
          <div style={{ color: "red", padding: "30px 20px", textAlign: "center" }}>
            {error}
          </div>
        ) : !details ? (
          <p className="unavailable-text" style={{ textAlign: "center", padding: "30px 0" }}>
            No edit details available for this appointment.
          </p>
        ) : (
          <div className="log-details-content">
            {/* Top Metadata Grid (Activity ID & Appointment Cluster ID IGNORED per instructions) */}
            <div className="log-metadata-grid">
              <div className="metadata-item">
                <span className="metadata-label">Center Name</span>
                <span className="metadata-value">{logData.centerName || "N/A"}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Center Type</span>
                <span className="metadata-value">{logData.centerType || "N/A"}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">City</span>
                <span className="metadata-value">{logData.city || "N/A"}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Edited On</span>
                <span className="metadata-value">
                  {formatDateDisplay(details.edited_on || logData.editedOn)}
                </span>
              </div>
            </div>

            {/* Dates Section */}
            {hasDatesSection && (
              <div className="log-card-section">
                <h4 className="section-heading">Dates</h4>
                <table className="log-card-table">
                  <thead>
                    <tr>
                      <th style={{ width: "90px" }}></th>
                      <th>WeekDay / Start Date</th>
                      <th>SlotDesign / End Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="row-type-label">Old</td>
                      <td>{formatDateDisplay(details.dates?.old?.start_date)}</td>
                      <td>{formatDateDisplay(details.dates?.old?.end_date)}</td>
                    </tr>
                    <tr>
                      <td className="row-type-label">New</td>
                      <td>{formatDateDisplay(details.dates?.new?.start_date)}</td>
                      <td>{formatDateDisplay(details.dates?.new?.end_date)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Slot Design Section */}
            {hasTemplateSection && (
              <div className="log-card-section">
                <h4 className="section-heading">Slot Design</h4>
                <table className="log-card-table">
                  <thead>
                    <tr>
                      <th style={{ width: "90px" }}></th>
                      <th style={{ width: "40%" }}>Week Days</th>
                      <th>Slot Design</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="row-type-label" style={{ verticalAlign: "top" }}>
                        Old
                      </td>
                      <td colSpan={2} style={{ padding: 0 }}>
                        <div className="slot-design-subtable">
                          {oldDayKeys.length > 0 ? (
                            oldDayKeys.map((day) => (
                              <div key={day} className="slot-design-row">
                                <span className="day-col">{day}</span>
                                <span className="design-col">
                                  {getTemplateDisplayValue(oldTemplateMap[day])}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="slot-design-row">
                              <span className="day-col">No data</span>
                              <span className="design-col">none</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="row-type-label" style={{ verticalAlign: "top" }}>
                        New
                      </td>
                      <td colSpan={2} style={{ padding: 0 }}>
                        <div className="slot-design-subtable">
                          {newDayKeys.length > 0 ? (
                            newDayKeys.map((day) => (
                              <div key={day} className="slot-design-row">
                                <span className="day-col">{day}</span>
                                <span className="design-col">
                                  {getTemplateDisplayValue(newTemplateMap[day])}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="slot-design-row">
                              <span className="day-col">No data</span>
                              <span className="design-col">none</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Impact Summary Numbers */}
            <div className="log-impact-summary-grid">
              <div className="impact-item">
                <span className="impact-label">No. of Impacted Slots</span>
                <span className="impact-value">{details.impacted_slots ?? 0}</span>
              </div>
              <div className="impact-item">
                <span className="impact-label">No. of Impacted Bookings</span>
                <span className="impact-value">{details.impacted_bookings ?? 0}</span>
              </div>
            </div>

            {/* Cancelled Bookings Section */}
            {details.cancelled_bookings && details.cancelled_bookings.length > 0 && (
              <div className="log-card-section">
                <h4 className="section-heading">Cancelled Bookings</h4>
                <table className="log-card-table">
                  <thead>
                    <tr>
                      <th>Slot ID</th>
                      <th>Date</th>
                      <th>Slot</th>
                      <th>Booking ID's</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.cancelled_bookings.map((slotItem, sIdx) => (
                      <tr key={sIdx}>
                        <td>{slotItem.slot_id || `Slt_ID_${String(sIdx + 1).padStart(3, "0")}`}</td>
                        <td>{formatDateDisplay(slotItem.date)}</td>
                        <td>{slotItem.slot_time || "N/A"}</td>
                        <td>
                          <div className="booking-list-column">
                            {slotItem.bookings && slotItem.bookings.length > 0 ? (
                              slotItem.bookings.map((b, bIdx) => (
                                <div key={bIdx}>
                                  {b.child_name || b.emrId || b._id || b.user_id || "Booking ID"}
                                </div>
                              ))
                            ) : (
                              <div>No booking IDs</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Created Appointments Section */}
            {details.created_appointments && details.created_appointments.length > 0 && (
              <div className="log-card-section">
                <h4 className="section-heading">Created Appointments</h4>
                <table className="log-card-table">
                  <thead>
                    <tr>
                      <th>Slot ID</th>
                      <th>Date</th>
                      <th>Slot</th>
                      {/* <th>Booking ID's</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {details.created_appointments.map((slotItem, sIdx) => (
                      <tr key={sIdx}>
                        <td>{slotItem.slot_id || `Slt_ID_${String(sIdx + 1).padStart(3, "0")}`}</td>
                        <td>{formatDateDisplay(slotItem.date)}</td>
                        <td>{slotItem.slot_time || "N/A"}</td>
                        {/* <td>
                          <div className="booking-list-column">
                            {slotItem.bookings && slotItem.bookings.length > 0 ? (
                              slotItem.bookings.map((b: any, bIdx: number) => (
                                <div key={bIdx}>
                                  {b.child_name || b.emrId || b._id || b.user_id || "Appointment ID"}
                                </div>
                              ))
                            ) : (
                              <div>No appointment IDs</div>
                            )}
                          </div>
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Modal Footer Actions */}
        <div className="log-modal-footer">
          <button className="btn-log-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLogDetailsModal;